import type { OnboardingCardBinding, UniversalOnboardingCardBinding } from '../services/funnels';
import type { TemplateFamilyMetadata } from './templateFamilyMetadata.ts';

export type OnboardingTemplateDesign = {
  templateName: string;
  theme: string;
  styleSheet: string;
  palette: string;
  primaryColor: string;
  secondaryColor: string;
  coverStyle: string;
  contractVersion: string;
  familyMetadata: TemplateFamilyMetadata;
};

export type OnboardingTemplateOption = {
  id: string;
  source: 'universal' | 'legacy';
  pageStyleId: string;
  baseTemplateId: string;
  templateDbId: number;
  templateType: string;
  templateFamily: string;
  name: string;
  description: string;
  previewUrl: string | null;
  thumbnailUrl: string | null;
  previewGradient?: string | null;
  isStyleVariant: boolean;
  kind: 'html' | 'image' | 'element';
  contractVersion: string;
  familyMetadata: TemplateFamilyMetadata;
  design?: OnboardingTemplateDesign;
  isBrandBase?: boolean;
  brandLogoUrl?: string | null;
  brandName?: string;
  universalStyleSelection?: {
    styleEngineVersion: 'universal_slots_v1';
    catalogReleaseId: string;
    pageStyleId: string;
    wizardDefaultComposition: Array<{
      instanceKey: string;
      sectionRole: string;
      targetDesignId: string;
      displayName: string;
      order: number;
      enabled: boolean;
    }>;
  };
};

const humanizeInstanceKey = (value: string) => value
  .replace(/[-_]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isUniversalOnboardingCard = (
  binding: OnboardingCardBinding,
): binding is UniversalOnboardingCardBinding => binding.source === 'universal';

export const getOnboardingBindingOptionId = (binding: OnboardingCardBinding) =>
  isUniversalOnboardingCard(binding)
    ? `universal:${binding.catalog_release_id}:${binding.page_style_id}`
    : `${binding.family_key}:${binding.binding_key}`;

export const isSelectableOnboardingCard = (binding: OnboardingCardBinding) =>
  isUniversalOnboardingCard(binding) || binding.kind === 'html' || binding.kind === 'image';

export const buildOnboardingOptionsFromBindings = (
  bindings: OnboardingCardBinding[],
): OnboardingTemplateOption[] =>
  bindings
    .filter(isSelectableOnboardingCard)
    .map((binding): OnboardingTemplateOption | null => {
      if (isUniversalOnboardingCard(binding)) {
        const isComplete =
          binding.style_engine_version === 'universal_slots_v1' &&
          Boolean(binding.catalog_release_id.trim()) &&
          Boolean(binding.page_style_id.trim()) &&
          Boolean(binding.page_style_checksum.trim()) &&
          Number.isInteger(binding.role_schema_version) &&
          Number.isInteger(binding.page_style_version) &&
          Array.isArray(binding.default_composition) &&
          binding.default_composition.length > 0 &&
          binding.default_composition.every((item) =>
            Boolean(item.instance_key?.trim()) &&
            Boolean(item.section_role?.trim()) &&
            Boolean(item.target_design_id?.trim()));
        if (!isComplete) return null;
        return {
          id: getOnboardingBindingOptionId(binding),
          source: 'universal',
          pageStyleId: binding.page_style_id,
          baseTemplateId: binding.page_style_id,
          templateDbId: 0,
          templateType: `universal:${binding.catalog_release_id}`,
          templateFamily: `universal:${binding.catalog_release_id}`,
          name: binding.display_name,
          description: binding.description ?? '',
          previewUrl: binding.preview_url,
          thumbnailUrl: binding.thumbnail_url || binding.preview_url,
          previewGradient: binding.preview_gradient,
          isStyleVariant: true,
          kind: 'html' as const,
          contractVersion: 'universal_slots_v1',
          familyMetadata: {},
          universalStyleSelection: {
            styleEngineVersion: 'universal_slots_v1' as const,
            catalogReleaseId: binding.catalog_release_id,
            pageStyleId: binding.page_style_id,
            wizardDefaultComposition: binding.default_composition.map((item, index) => ({
              instanceKey: item.instance_key,
              sectionRole: item.section_role,
              targetDesignId: item.target_design_id,
              displayName: humanizeInstanceKey(item.instance_key),
              order: index,
              enabled: true,
            })),
          },
        };
      }
      const isHtml = binding.kind === 'html';
      const familyMetadata = binding.family_metadata ?? {};
      return {
        id: getOnboardingBindingOptionId(binding),
        source: 'legacy',
        pageStyleId: binding.style_id,
        baseTemplateId: binding.family_key,
        templateDbId: binding.id,
        templateType: binding.family_key,
        templateFamily: binding.family_key,
        name: binding.display_name,
        description: binding.description ?? '',
        previewUrl: binding.preview_url || null,
        thumbnailUrl: binding.thumbnail_url || binding.preview_url || null,
        isStyleVariant: isHtml,
        kind: binding.kind,
        contractVersion: binding.contract_version,
        familyMetadata,
        design: isHtml
          ? {
              templateName: binding.display_name,
              theme: binding.style_id,
              styleSheet: binding.style_id,
              palette: binding.palette_fallback,
              primaryColor: '#FFFFFF',
              secondaryColor: '#FFFFFF',
              coverStyle: 'template_studio_html',
              contractVersion: binding.contract_version,
              familyMetadata,
            }
          : undefined,
      };
    })
    .filter((option): option is OnboardingTemplateOption => Boolean(option));

export const getOnboardingPreviewFallbackStyle = (
  template: Pick<OnboardingTemplateOption, 'previewGradient'>,
) => template.previewGradient
  ? { background: template.previewGradient }
  : undefined;

export const getOnboardingTemplateCardPresentation = (
  template: Pick<
    OnboardingTemplateOption,
    'id' | 'source' | 'pageStyleId' | 'name' | 'description'
  >,
) => ({
  optionId: template.id,
  source: template.source,
  pageStyleId: template.pageStyleId,
  name: template.name,
  description: template.description,
  ariaLabel: template.source === 'universal'
    ? `Seleccionar estilo universal ${template.name}`
    : `Seleccionar plantilla ${template.name}`,
  showUniversalIndicator: template.source === 'universal',
});

export const toggleOnboardingTemplateSelection = (
  selectedTemplateOrder: string[],
  templateId: string,
) => selectedTemplateOrder.includes(templateId)
  ? selectedTemplateOrder.filter((id) => id !== templateId)
  : [...selectedTemplateOrder, templateId];
